import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { Spin } from 'antd';
import SearchBar from '../../../../../shared/components/SearchBar';
import { useAssetOverview } from '../../../../../context/AssetOverviewContext';
import { useAssetList } from '../../../../../hooks/useAssetList';
import { useVirtualList, type VisibleRange } from '../../../../../hooks/useVirtualList';
import { AssetSortOrder } from '../../../../../enums/assetSortOrder';
import type { AssetListItem } from '../../../../../types/InfrastructureManagerTypes';
import { toAssetKey } from '../../assetList.utils';
import { ASSET_LIST_EMPTY_COPY, ASSET_ROW_HEIGHT_REM, SEARCH_DEBOUNCE_MS } from '../../constants';
import AssetListHeader from './AssetListHeader';
import AssetListRow from './AssetListRow';
import styles from './AssetListPanel.module.scss';

const INITIAL_RANGE: VisibleRange = { start: 0, end: 0 };
const RANGE_SETTLE_MS = 120;
const NO_EXPANDED = new Set<string>();

const AssetListPanel = () => {
  const { config, isLoadingConfig, isConfigLoadingError, search, setSearch, selected, toggleSelect, activeStatisticId, activeStatistic } = useAssetOverview();
  // Raw input stays here; only the settled term reaches the shared context.
  const [searchInput, setSearchInput] = useState(search);
  const [sortOrder, setSortOrder] = useState(AssetSortOrder.ASCENDING);
  // Read once: collapsed rows have this exact height, so they are never measured.
  const [rowHeight] = useState(() => parseFloat(getComputedStyle(document.documentElement).fontSize) * ASSET_ROW_HEIGHT_REM);

  const configVersion = config?.updatedAt ?? null;
  const isConfigured = !!config?.rows.length;

  // Expansion belongs to one config version, so a saved config starts everything collapsed.
  const [expansion, setExpansion] = useState({ configVersion, keys: NO_EXPANDED });
  const expandedKeys = expansion.configVersion === configVersion ? expansion.keys : NO_EXPANDED;

  // The pager needs the visible rows and the virtualizer needs the pager's total, so the range round-trips through state.
  const [range, setRange] = useState(INITIAL_RANGE);
  const { total, getAsset, findAssetIndex, isLoadingAssets, isFetchingAssets, isAssetsLoadingError } = useAssetList({
    configVersion,
    isConfigured,
    statisticId: activeStatistic?.id ?? null,
    search,
    sort: sortOrder,
    range,
  });
  const {
    scrollRef,
    measureRef,
    items,
    range: visibleRange,
    scrollSize,
    scrollToIndex,
  } = useVirtualList({ count: total, estimateSize: rowHeight, resetKey: `${configVersion}|${activeStatisticId}|${search}|${sortOrder}` });

  // Settles before fetching, so dragging the scrollbar across thousands of rows requests only where it stops.
  useEffect(() => {
    const timer = setTimeout(() => {
      setRange((current) => (current.start === visibleRange.start && current.end === visibleRange.end ? current : visibleRange));
    }, RANGE_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [visibleRange]);

  // A map selection scrolls its row into view if its page is loaded. Keyed on the selection only, so later page loads never pull the list back.
  useEffect(() => {
    if (!selected) return;
    const index = findAssetIndex(selected);
    if (index >= 0) scrollToIndex(index);
  }, [selected]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, setSearch]);

  const handleToggleSort = useCallback(() => {
    setSortOrder((current) => (current === AssetSortOrder.ASCENDING ? AssetSortOrder.DESCENDING : AssetSortOrder.ASCENDING));
  }, []);

  const handleToggleExpanded = useCallback(
    (assetKey: string) => {
      setExpansion((current) => {
        const keys = new Set(current.configVersion === configVersion ? current.keys : NO_EXPANDED);
        if (!keys.delete(assetKey)) keys.add(assetKey);
        return { configVersion, keys };
      });
    },
    [configVersion],
  );

  const handleToggleSelect = useCallback(
    ({ layerId, keyValue, displayValue, bbox }: AssetListItem) => toggleSelect({ layerId, keyValue, displayValue, bbox }),
    [toggleSelect],
  );

  const selectedKey = selected ? toAssetKey(selected) : null;
  const listStyle = { '--asset-list-height': `${scrollSize}px` } as CSSProperties;
  const isBusy = isLoadingConfig || isLoadingAssets;

  // Held back while loading so the spinner isn't captioned; a failed load reads as an error, never as "no assets".
  let emptyMessage: string | null = null;
  if (!isBusy) {
    if (isConfigLoadingError || (isAssetsLoadingError && !total)) emptyMessage = ASSET_LIST_EMPTY_COPY.LOAD_ERROR;
    else if (!isConfigured && activeStatisticId === null) emptyMessage = ASSET_LIST_EMPTY_COPY.NO_ASSETS;
    else if (!total && search) emptyMessage = ASSET_LIST_EMPTY_COPY.NO_MATCHES;
    else if (!total) emptyMessage = activeStatisticId === null ? ASSET_LIST_EMPTY_COPY.NO_RESULTS : ASSET_LIST_EMPTY_COPY.NO_STATISTIC_MATCHES;
  }

  return (
    <section className={styles.assetListPanelRoot}>
      <SearchBar value={searchInput} onChange={setSearchInput} placeholder="SEARCH ASSETS" className={styles.assetListSearch} />

      <div className={styles.assetListCard}>
        <AssetListHeader sortOrder={sortOrder} onToggleSort={handleToggleSort} />

        {isBusy ? (
          <div className={styles.assetListLoading}>
            <Spin size="small" />
          </div>
        ) : null}

        {emptyMessage ? <p className={styles.assetListEmpty}>{emptyMessage}</p> : null}

        {!isBusy && total > 0 ? (
          <div ref={scrollRef} className={styles.assetList}>
            <div role="list" aria-label="Assets" aria-busy={isFetchingAssets} className={styles.assetListInner} style={listStyle}>
              {items.map(({ index, top }) => {
                const asset = getAsset(index);
                const positionStyle = { '--asset-row-top': `${top}px` } as CSSProperties;
                if (!asset)
                  return <div key={`pending-${index}`} role="listitem" aria-busy className={styles.assetListSkeleton} style={positionStyle} />;
                const assetKey = toAssetKey(asset);
                return (
                  <AssetListRow
                    key={assetKey}
                    asset={asset}
                    assetKey={assetKey}
                    index={index}
                    top={top}
                    isExpanded={expandedKeys.has(assetKey)}
                    isSelected={assetKey === selectedKey}
                    measureRef={measureRef}
                    onToggleExpanded={handleToggleExpanded}
                    onToggleSelect={handleToggleSelect}
                  />
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default AssetListPanel;
